/**
 * @brisa/shared/observability/hash-email
 *
 * SHA-256 hex de email normalizado (lower + trim).
 *
 * Garantias:
 *   - Mesma semântica que `encode(digest(lower(trim(email)), 'sha256'), 'hex')` em SQL.
 *   - Determinístico cross-runtime (Node 20+, edge, browser via WebCrypto).
 *   - Síncrono via API Node `node:crypto` no server (single roundtrip).
 *   - Async em browser (WebCrypto subtle.digest é async); função exporta variante async
 *     para casos onde o caller já está em async context.
 *
 * Uso (server-side, em Server Actions):
 *   import { hashEmail } from '@brisa/shared/observability';
 *   const email_hash = hashEmail(input.email);
 *
 * Cruzamento Hipótese 3 (PRD): comparar hashEmail(profile.email) contra
 * waitlist_signups.email_hash para popular `was_waitlisted_email`.
 */

function normalize(email: string): string {
  return email.toLowerCase().trim();
}

/**
 * Versão síncrona — usa Node `crypto` (sem prefixo `node:` por compat com webpack
 * Next 14, conforme padrão usado em feature-flags/resolver.ts).
 *
 * No browser, lança se chamado fora de Node — preferir hashEmailAsync nesse caso.
 *
 * No edge runtime do Next, `require('crypto')` falha — usar hashEmailAsync que
 * detecta WebCrypto subtle.
 */
export function hashEmail(email: string): string {
  const input = normalize(email);
  if (typeof window !== 'undefined') {
    throw new Error(
      'hashEmail (sync) only works in Node. Use hashEmailAsync() in the browser/edge.',
    );
  }
  // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires
  const nodeCrypto: typeof import('crypto') = require('crypto');
  return nodeCrypto.createHash('sha256').update(input, 'utf8').digest('hex');
}

/**
 * Versão async — funciona em qualquer ambiente (Node, edge, browser).
 * Use no client-side se precisar hashar email em browser.
 */
export async function hashEmailAsync(email: string): Promise<string> {
  const input = normalize(email);
  const subtle =
    typeof globalThis.crypto !== 'undefined' && globalThis.crypto.subtle
      ? globalThis.crypto.subtle
      : undefined;
  if (subtle) {
    const bytes = new TextEncoder().encode(input);
    const digest = await subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  const nodeCrypto: typeof import('crypto') = await import('crypto');
  return nodeCrypto.createHash('sha256').update(input, 'utf8').digest('hex');
}
